import axios, { type AxiosResponse } from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";

import { settings } from "@krea/customer-features/settings";
import { useAuthStore } from "@krea/shared/features/auth/authStore";
import { AppName } from "@krea/shared/types/common";

import { useCustomerFeaturesSettingsStore } from "../store/app-settings/useCustomerFeaturesSettingsStore";
import { queryClient, USER_STORE_KEY } from "../store/queryClient";

const onAuthorizationFailed = () => {
  queryClient.setQueryData([USER_STORE_KEY], null);
  // queryClient.resetQueries({ queryKey: [USER_STORE_KEY] });
};

let ongoingRefreshRequest: Promise<
  AxiosResponse<IRefreshTokenResponse>
> | null = null;

interface IRefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

const refreshAndSetTokens = async (token: string) => {
  const axiosInstanceWithoutInterceptor = axios.create();
  const appName = useCustomerFeaturesSettingsStore.getState().appName;
  const isPartnerWeb = appName === AppName.PARTNER;
  const { setAccessToken, setRefreshToken, reset } = useAuthStore.getState();

  const Url = `${settings.backendBaseUrl}${
    isPartnerWeb ? "/partner" : ""
  }/api/v1/auth/refresh-token`;

  if (!ongoingRefreshRequest) {
    ongoingRefreshRequest =
      axiosInstanceWithoutInterceptor.post<IRefreshTokenResponse>(Url, {
        refreshToken: token,
      });
  }

  try {
    const response = await ongoingRefreshRequest;

    const responseData = response.data;

    setAccessToken(responseData.accessToken);
    setRefreshToken(responseData.refreshToken);

    ongoingRefreshRequest = null;

    return responseData.accessToken;
  } catch (error) {
    const status = (error as { response?: { status: number } }).response
      ?.status;

    if (status && [401, 403].includes(status)) {
      // If the refresh token is invalid, we should remove the tokens from the local storage.
      reset();
    } else {
      console.error("Failed to refresh token", error);
    }

    ongoingRefreshRequest = null;

    return null;
  }
};

const checkAccessAndRefreshTokens = () => {
  const {
    accessToken: storedAccessToken,
    refreshToken: storedRefreshToken,
    reset,
  } = useAuthStore.getState();

  if (!storedAccessToken || !storedRefreshToken) {
    console.warn("Not logged in");

    return null;
  }

  const decodedAccessToken = jwtDecode(storedAccessToken);
  const decodedRefreshToken = jwtDecode(storedRefreshToken);

  const hasAccessTokenExpired =
    decodedAccessToken.exp && decodedAccessToken.exp - dayjs().unix() <= 0;
  const hasRefreshTokenExpired =
    decodedRefreshToken.exp && decodedRefreshToken.exp - dayjs().unix() <= 0;

  if (hasRefreshTokenExpired) {
    console.warn("Refresh token has expired");

    reset();
    // localStorage.setItem(
    //   "redirect",
    //   JSON.stringify({
    //     path: window.location.pathname + window.location.search,
    //     date: Date.now(),
    //     minuteDuration: 10,
    //   }),
    // );

    onAuthorizationFailed();

    return null;
  }

  if (hasAccessTokenExpired) {
    return refreshAndSetTokens(storedRefreshToken);
  }

  return storedAccessToken;
};

export const getAccessToken = async () => {
  const currentURL = new URL(window.location.href);

  // quickAuth could contain a very short-lived refreshToken so that we can "quickAuth" into customer web from
  // other authenticated sources.
  const quickAuthRefreshToken = currentURL.searchParams.get("quickAuth");
  currentURL.searchParams.delete("quickAuth");

  window.history.replaceState(null, "", currentURL.href);

  if (quickAuthRefreshToken) {
    try {
      const decodedQuickAuthToken = jwtDecode(quickAuthRefreshToken);

      const quickAuthTokenExpired =
        decodedQuickAuthToken.exp &&
        decodedQuickAuthToken.exp - dayjs().unix() <= 0;

      if (!quickAuthTokenExpired) {
        // If we have a quickAuthRefreshToken.
        // We want to use  that to get a fresh set of "normal" tokens.
        return await refreshAndSetTokens(quickAuthRefreshToken);
      }
    } catch (err) {
      console.error("Failed to decode quickAuth token", err);
    }
  }

  return checkAccessAndRefreshTokens();
};

// eslint-disable-next-line import/no-default-export
export default class HttpService {
  static instance: HttpService | null;

  static getInstance() {
    if (HttpService.instance) {
      return HttpService.instance;
    }

    HttpService.instance = new HttpService();

    return HttpService.instance;
  }

  static createInstanceWithoutInterceptors() {
    return axios.create({
      baseURL: settings.backendBaseUrl,
    });
  }

  setupInterceptors() {
    axios.defaults.withCredentials = false; // otherwise axios seems to send session-cookie credentials SOMETIMES

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: { response?: unknown }) => {
        return Promise.reject(error.response as Error);
      },
    );

    axios.interceptors.request.use(
      async (config) => {
        const currentAccessToken = await getAccessToken();
        config.headers.Authorization = currentAccessToken
          ? `Bearer ${currentAccessToken}`
          : null;

        const params = new URLSearchParams(window.location.search);
        const auth = params.get("auth");

        if (auth) {
          return { ...config, params: { ...config.params, auth } };
        }

        return config;
      },
      (error) => {
        // Do something with request error
        return Promise.reject(error as Error);
      },
    );
  }
}
